import moment from 'moment';

import {Campaign, CampaignErrorCodes} from '../../../../types/src';

import {CONSTANTS} from '../constants/constants';
import {isCampaignAcquisition} from './isCampaignAcquisition';
import {isCampaignInmail} from './isCampaignInmail';
import {isCampaignMessageReqeust} from './isCampaignMessageRequest';
import {isCampaignRetention} from './isCampaignRetention';

export type CampaignCompletionState = {
  completionErrorCode: CampaignErrorCodes | null;
  isArchiveOnly: boolean;
  isFinished: boolean;
};

function isExternalSourceOutdated(campaign: Campaign) {
  return !campaign.last_external_source_date || moment().diff(campaign.last_external_source_date, 'days') >= 30;
}

function getRemainingAudienceCount(campaign: Campaign) {
  if (typeof campaign?.stats?.to_be_processed === 'number') {
    return campaign.stats.to_be_processed;
  }

  if (typeof campaign?.stats?.reach === 'number') {
    return campaign.stats.reach;
  }

  return null;
}

function isParsingNotFinished(campaign: Campaign) {
  const campaignUser = campaign.campaign_users ? campaign.campaign_users[0] : {};
  const isEmailInvites = campaign.type === CONSTANTS.campaignTypes.acquisition_via_import;
  const isRegularCampaign =
    campaign.type === CONSTANTS.campaignTypes.acquisition || campaign.type === CONSTANTS.campaignTypes.retention;
  const isParsingNotFinishedEmailInvites = isEmailInvites && campaignUser && !campaign.last_scan_date;
  const isParsingNotFinishedRegularCampaign = isRegularCampaign && campaignUser && campaignUser.loop === 0;

  return isParsingNotFinishedEmailInvites || isParsingNotFinishedRegularCampaign;
}

export function getCampaignCompletionErrorCode(campaign: Campaign): CampaignErrorCodes | null {
  if (CONSTANTS.ENDLESS_CAMPAIGN_TYPES.includes(campaign.type)) {
    return null;
  }

  if (isCampaignRetention(campaign)) {
    return CampaignErrorCodes.UpdateRetention;
  }

  if (campaign.type === CONSTANTS.campaignTypes.acquisition_via_import) {
    return CampaignErrorCodes.EmailInvitesAllMessagesSent;
  }

  if (isCampaignMessageReqeust(campaign)) {
    return CampaignErrorCodes.MessageRequest;
  }

  if (isCampaignInmail(campaign)) {
    return CampaignErrorCodes.AllMessagesSent;
  }

  if (campaign.type?.includes('acquisition_csv')) {
    return CampaignErrorCodes.UpdateCsv;
  }

  if (isCampaignAcquisition(campaign)) {
    return CampaignErrorCodes.UpdateSearchUrl;
  }

  return null;
}

export function getCampaignCompletionState(campaign: Campaign): CampaignCompletionState {
  if (!campaign) {
    return {
      completionErrorCode: null,
      isArchiveOnly: false,
      isFinished: false,
    };
  }

  if (CONSTANTS.ENDLESS_CAMPAIGN_TYPES.includes(campaign.type)) {
    const isFinished = Boolean(campaign.last_external_source_date && moment().diff(campaign.last_external_source_date, 'days') >= 30);

    return {
      completionErrorCode: null,
      isArchiveOnly: isFinished,
      isFinished,
    };
  }

  const remainingAudienceCount = getRemainingAudienceCount(campaign);
  const inSequenceCount = campaign?.stats?.in_sequence;
  const hasPotential = Number(campaign.potential) > 0;
  const isFinished =
    !campaign.archived &&
    hasPotential &&
    typeof remainingAudienceCount === 'number' &&
    remainingAudienceCount <= 0 &&
    typeof inSequenceCount === 'number' &&
    inSequenceCount <= 0 &&
    !isParsingNotFinished(campaign) &&
    !campaign.has_manual_sequence &&
    isExternalSourceOutdated(campaign);


  const shouldReturnCompletionErrorCode =
    isFinished &&
    // If invite action is intentionally disabled for acquisition campaigns,
    // do not auto-attach "update search url" completion error again.
    (!isCampaignAcquisition(campaign) && Boolean(campaign.messages_active));

  return {
    completionErrorCode: shouldReturnCompletionErrorCode ? getCampaignCompletionErrorCode(campaign) : null,
    isArchiveOnly: false,
    isFinished,
  };
}
